import React, { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CoverDesignTab = ({ designs, onSelectDesign, onCustomize, onRegenerate }) => {
  const [selectedDesign, setSelectedDesign] = useState(0);
  const [isRegenerating, setIsRegenerating] = useState(false);

  const handleRegenerate = async () => {
    setIsRegenerating(true);
    await onRegenerate();
    setIsRegenerating(false);
  };

  const currentDesign = designs?.[selectedDesign];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2">
        <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <h3 className="text-lg font-heading font-semibold text-foreground">
              Cover Preview
            </h3>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                iconName="Edit2"
                iconPosition="left"
                onClick={() => onCustomize(currentDesign)}
              >
                Customize
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="RefreshCw"
                iconPosition="left"
                loading={isRegenerating}
                onClick={handleRegenerate}
              >
                Regenerate
              </Button>
            </div>
          </div>

          <div className="p-8 bg-muted/30 flex items-center justify-center min-h-[600px]">
            <div className="relative max-w-md w-full aspect-[2/3] bg-background rounded-lg shadow-2xl overflow-hidden">
              <Image
                src={currentDesign?.frontCover}
                alt={currentDesign?.frontCoverAlt}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          <div className="p-4 border-t border-border">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground mb-2">Front Cover</p>
                <div className="aspect-[2/3] bg-muted rounded-md overflow-hidden">
                  <Image
                    src={currentDesign?.frontCover}
                    alt={currentDesign?.frontCoverAlt}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-2">Back Cover</p>
                <div className="aspect-[2/3] bg-muted rounded-md overflow-hidden">
                  <Image
                    src={currentDesign?.backCover}
                    alt={currentDesign?.backCoverAlt}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="lg:col-span-1 space-y-6">
        <div className="bg-card border border-border rounded-lg shadow-sm">
          <div className="p-4 border-b border-border">
            <h3 className="text-sm font-heading font-semibold text-foreground">
              Design Options
            </h3>
          </div>
          <div className="p-2 space-y-2 max-h-[400px] overflow-y-auto">
            {designs?.map((design, index) => (
              <button
                key={design?.id}
                onClick={() => setSelectedDesign(index)}
                className={`w-full p-3 rounded-md border transition-all duration-200 ${
                  selectedDesign === index
                    ? 'border-primary bg-primary/10' :'border-border hover:border-primary/50 hover:bg-muted/50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-16 aspect-[2/3] bg-muted rounded overflow-hidden flex-shrink-0">
                    <Image
                      src={design?.thumbnail}
                      alt={design?.thumbnailAlt}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {design?.name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {design?.style}
                    </p>
                  </div>
                  {selectedDesign === index && (
                    <Icon name="Check" size={18} className="text-primary flex-shrink-0" />
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg shadow-sm">
          <div className="p-4 border-b border-border">
            <h3 className="text-sm font-heading font-semibold text-foreground">
              Design Details
            </h3>
          </div>
          <div className="p-4 space-y-3">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Style</p>
              <p className="text-sm font-medium text-foreground">{currentDesign?.style}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Color Scheme</p>
              <div className="flex items-center gap-2">
                {currentDesign?.colors?.map((color, index) => (
                  <div
                    key={index}
                    className="w-8 h-8 rounded-md border border-border"
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Typography</p>
              <p className="text-sm font-medium text-foreground">{currentDesign?.typography}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">AI Confidence</p>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-success transition-all duration-400"
                    style={{ width: `${currentDesign?.confidence}%` }}
                  />
                </div>
                <span className="text-xs font-mono text-muted-foreground">
                  {currentDesign?.confidence}%
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg shadow-sm">
          <div className="p-4 border-b border-border">
            <h3 className="text-sm font-heading font-semibold text-foreground">
              Competitor Analysis
            </h3>
          </div>
          <div className="p-4 space-y-3">
            <p className="text-xs text-muted-foreground">
              Based on top-performing books in your genre:
            </p>
            <ul className="space-y-2">
              {currentDesign?.competitorInsights?.map((insight, index) => (
                <li key={index} className="flex items-start gap-2 text-xs text-foreground">
                  <Icon name="TrendingUp" size={12} className="text-success mt-0.5 flex-shrink-0" />
                  <span>{insight}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoverDesignTab;